import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSubmitted(true);
        } catch (err) {
            // Endpoint always returns 200 — but show friendly fallback just in case
            toast.error("Could not send reset link. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="forgot-success">
                <div className="w-full max-w-md text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#E53935] mx-auto mb-5" strokeWidth={1.5} />
                    <h1 className="font-heading text-3xl text-[#1C252E] font-medium tracking-tight mb-3">Check your inbox</h1>
                    <p className="text-sm text-[#4A5568] mb-6">
                        If an account exists for <span className="font-medium text-[#1C252E]">{email}</span>, we've sent a password-reset link. The link expires in 60 minutes.
                    </p>
                    <Link
                        to="/login"
                        data-testid="forgot-back-to-login"
                        className="inline-flex items-center gap-2 text-[#E53935] hover:underline text-sm font-medium"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                        Back to sign in
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16" data-testid="forgot-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Account recovery</p>
                    <h1 className="font-heading text-4xl text-[#1C252E] font-medium tracking-tight" data-testid="forgot-heading">
                        Forgot your password?
                    </h1>
                    <p className="text-sm text-[#4A5568] mt-3 max-w-sm mx-auto">
                        Enter the email you signed up with. We'll send a one-time reset link.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="bg-white border border-black/5 rounded-3xl p-8 space-y-4">
                    <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                        <label htmlFor="forgot-email" className="sr-only">Email</label>
                        <input
                            id="forgot-email"
                            type="email"
                            placeholder="Email address"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="forgot-email-input"
                            className="w-full rounded-xl border border-black/10 pl-11 pr-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="forgot-submit-btn"
                        className="w-full bg-[#1C252E] hover:bg-black text-white rounded-full py-3.5 font-medium transition-all disabled:opacity-60"
                    >
                        {loading ? "Sending…" : "Send reset link"}
                    </button>
                    <p className="text-center text-sm text-[#4A5568] pt-2">
                        Remembered it?{" "}
                        <Link to="/login" className="text-[#E53935] hover:underline font-medium" data-testid="forgot-signin-link">
                            Sign in
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;
